import React, {
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import {useSelector} from 'react-redux';

import {ApiContext} from '../../../../api/Api';
import CustomCheckbox from '../../../../elements/CustomCheckbox/CustomCheckbox';
import Card from '../../../../components/Card/Card';

const OperationalPolicies = () => {
    const api = useContext(ApiContext);

    const lounges = useSelector(
        ({api}) => api.lounges || []
    );

    const [orderPolicies, setOrderPolicies] = useState({});
    const [ticketPolicies, setTicketPolicies] = useState({});

    const [savingOrder, setSavingOrder] = useState(false);
    const [savingTicket, setSavingTicket] = useState(false);

    const [orderMessage, setOrderMessage] = useState('');
    const [ticketMessage, setTicketMessage] = useState('');

    useEffect(() => {
        api.lounges.get2({
            filters: {
                isLounge: true
            }
        });
    }, [api]);

    const realLounges = useMemo(
        () => lounges.filter(
            lounge => lounge && lounge.id
        ),
        [lounges]
    );

    /*
     * Compatibilidad de envío de comandas:
     *
     * Si order.allowSend todavía no existe,
     * la operación continúa habilitada.
     */
    useEffect(() => {
        setOrderPolicies(previous => {
            const next = {...previous};

            realLounges.forEach(lounge => {
                if (
                    Object.prototype.hasOwnProperty.call(
                        next,
                        lounge.id
                    )
                ) {
                    return;
                }

                const config =
                    lounge.config
                    && typeof lounge.config === 'object'
                        ? lounge.config
                        : {};

                const order =
                    config.order
                    && typeof config.order === 'object'
                        ? config.order
                        : {};

                next[lounge.id] =
                    order.allowSend !== false;
            });

            return next;
        });
    }, [realLounges]);

    /*
     * Compatibilidad del ticket de ingreso:
     *
     * La impresión permanece apagada salvo que
     * admissionTicket.enabled sea explícitamente true.
     */
    useEffect(() => {
        setTicketPolicies(previous => {
            const next = {...previous};

            realLounges.forEach(lounge => {
                if (
                    Object.prototype.hasOwnProperty.call(
                        next,
                        lounge.id
                    )
                ) {
                    return;
                }

                const config =
                    lounge.config
                    && typeof lounge.config === 'object'
                        ? lounge.config
                        : {};

                const admissionTicket =
                    config.admissionTicket
                    && typeof config.admissionTicket === 'object'
                        ? config.admissionTicket
                        : {};

                next[lounge.id] =
                    admissionTicket.enabled === true;
            });

            return next;
        });
    }, [realLounges]);

    const toggleOrderLounge = loungeId => {
        setOrderMessage('');

        setOrderPolicies(previous => ({
            ...previous,
            [loungeId]: previous[loungeId] === false
        }));
    };

    const toggleTicketLounge = loungeId => {
        setTicketMessage('');

        setTicketPolicies(previous => ({
            ...previous,
            [loungeId]: previous[loungeId] !== true
        }));
    };

    const saveOrderPolicies = async () => {
        setSavingOrder(true);
        setOrderMessage('');

        try {
            await Promise.all(
                realLounges.map(lounge => {
                    const currentConfig =
                        lounge.config
                        && typeof lounge.config === 'object'
                            ? lounge.config
                            : {};

                    const currentOrder =
                        currentConfig.order
                        && typeof currentConfig.order === 'object'
                            ? currentConfig.order
                            : {};

                    const config = {
                        ...currentConfig,
                        order: {
                            ...currentOrder,
                            allowSend:
                                orderPolicies[lounge.id] !== false
                        }
                    };

                    return api.lounges.editConfig(
                        lounge.id,
                        config
                    );
                })
            );

            setOrderMessage(
                'Configuración de comandas guardada correctamente.'
            );
        } catch (error) {
            console.error(error);

            setOrderMessage(
                'No fue posible guardar la configuración de comandas.'
            );
        } finally {
            setSavingOrder(false);
        }
    };

    const saveTicketPolicies = async () => {
        setSavingTicket(true);
        setTicketMessage('');

        try {
            await Promise.all(
                realLounges.map(lounge => {
                    const currentConfig =
                        lounge.config
                        && typeof lounge.config === 'object'
                            ? lounge.config
                            : {};

                    const currentAdmissionTicket =
                        currentConfig.admissionTicket
                        && typeof currentConfig.admissionTicket === 'object'
                            ? currentConfig.admissionTicket
                            : {};

                    const config = {
                        ...currentConfig,
                        admissionTicket: {
                            ...currentAdmissionTicket,
                            enabled:
                                ticketPolicies[lounge.id] === true
                        }
                    };

                    return api.lounges.editConfig(
                        lounge.id,
                        config
                    );
                })
            );

            setTicketMessage(
                'Configuración de tickets de ingreso guardada correctamente.'
            );
        } catch (error) {
            console.error(error);

            setTicketMessage(
                'No fue posible guardar la configuración de tickets de ingreso.'
            );
        } finally {
            setSavingTicket(false);
        }
    };

    return (
        <div className="container-fluid main-container">
            <Card
                title="Políticas Operativas Da Vinci / Orozco"
                content={
                    <div>
                        <h4>Envío de comandas</h4>

                        <p>
                            Selecciona las salas en las que
                            estará habilitado el envío de comandas.
                        </p>

                        <div>
                            {realLounges.map(lounge => (
                                <CustomCheckbox
                                    key={lounge.id}
                                    label={lounge.name}
                                    inline
                                    isChecked={
                                        orderPolicies[lounge.id] !== false
                                    }
                                    onChange={() =>
                                        toggleOrderLounge(lounge.id)
                                    }
                                />
                            ))}
                        </div>

                        <div style={{marginTop: '15px'}}>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={savingOrder}
                                onClick={saveOrderPolicies}
                            >
                                {
                                    savingOrder
                                        ? 'Guardando...'
                                        : 'Guardar cambios'
                                }
                            </button>

                            {orderMessage && (
                                <span
                                    style={{
                                        marginLeft: '15px'
                                    }}
                                >
                                    {orderMessage}
                                </span>
                            )}
                        </div>

                        <hr/>

                        <h4>
                            Impresión automática de ticket de ingreso
                        </h4>

                        <p>
                            Selecciona las salas en las que
                            se imprimirá automáticamente el ticket
                            al registrar el ingreso.
                        </p>

                        <div>
                            {realLounges.map(lounge => (
                                <CustomCheckbox
                                    key={lounge.id}
                                    label={lounge.name}
                                    inline
                                    isChecked={
                                        ticketPolicies[lounge.id] === true
                                    }
                                    onChange={() =>
                                        toggleTicketLounge(lounge.id)
                                    }
                                />
                            ))}
                        </div>

                        <div style={{marginTop: '15px'}}>
                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={savingTicket}
                                onClick={saveTicketPolicies}
                            >
                                {
                                    savingTicket
                                        ? 'Guardando...'
                                        : 'Guardar cambios'
                                }
                            </button>

                            {ticketMessage && (
                                <span
                                    style={{
                                        marginLeft: '15px'
                                    }}
                                >
                                    {ticketMessage}
                                </span>
                            )}
                        </div>
                    </div>
                }
            />
        </div>
    );
};

export default OperationalPolicies;
